import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import Collapse from '@material-ui/core/Collapse';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import AddBox from '@material-ui/icons/AddBox';
import Tooltip from '@ublend-npm/react-tooltip';

import {
  CollapsableContainer,
  TitleBar,
  TitleText,
  ContentContainer,
  ActionsArea,
  FooterContainer,
  FooterButton,
  FooterButtonText,
  VisibilityOffIcon,
} from './Collapsable.styled';

import { IconButton, Icon } from './GhostIconButton.styled';

const Collapsable = ({
  id,
  expandedByDefault,
  title,
  titleDecoration,
  moreButtonVisible,
  moreButtonLabel,
  onMoreButtonClicked,
  hiddenButtonVisible,
  hiddenButtonLabel,
  footerVisible,
  footerActionLabel,
  onFooterActionClicked,
  children,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [timeout, setTimeoutDuration] = useState(
    expandedByDefault ? 0 : undefined
  );

  useEffect(() => {
    setExpanded(expandedByDefault);
  }, [expandedByDefault]);

  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
    setTimeoutDuration(undefined);
  }, [expanded]);

  const ExpandIcon = useMemo(
    () => (expanded ? ExpandLessIcon : ExpandMoreIcon),
    [expanded]
  );
  const expandLabel = useMemo(() => (expanded ? 'Collapse' : 'Expand'), [
    expanded,
  ]);

  const moreButton = moreButtonVisible ? (
    <Tooltip position="top" title={moreButtonLabel}>
      <IconButton aria-label={moreButtonLabel} onClick={onMoreButtonClicked}>
        <Icon fontSize="16px" IconType={MoreHorizIcon} />
      </IconButton>
    </Tooltip>
  ) : null;

  const hiddenButton = hiddenButtonVisible ? (
    <Tooltip position="top" title={hiddenButtonLabel}>
      <div role="button" tabIndex="0">
        <Icon fontSize="16px" IconType={VisibilityOffIcon} />
      </div>
    </Tooltip>
  ) : null;

  const footer = footerVisible ? (
    <FooterContainer>
      <FooterButton
        aria-label={footerActionLabel}
        onClick={onFooterActionClicked}
      >
        <Icon fontSize="20px" IconType={AddBox} />
        <FooterButtonText>{footerActionLabel}</FooterButtonText>
      </FooterButton>
    </FooterContainer>
  ) : null;

  return (
    <CollapsableContainer id={id}>
      <TitleBar
        id={`${id}-controls`}
        aria-expanded={expanded}
        aria-controls={`${id}-content`}
      >
        <TitleText>{title}</TitleText>
        <ActionsArea>
          {hiddenButton}
          {moreButton}
          <Tooltip position="top" title={expandLabel}>
            <IconButton aria-label={expandLabel} onClick={toggleExpanded}>
              <Icon fontSize="16px" IconType={ExpandIcon} />
            </IconButton>
          </Tooltip>
        </ActionsArea>
      </TitleBar>
      {titleDecoration}
      <Collapse
        in={expanded}
        id={`${id}-content`}
        aria-labelledby={`${id}-controls`}
        timeout={timeout}
      >
        <ContentContainer>{children}</ContentContainer>
        {footer}
      </Collapse>
    </CollapsableContainer>
  );
};

Collapsable.propTypes = {
  id: PropTypes.string.isRequired,
  expandedByDefault: PropTypes.bool,
  title: PropTypes.string.isRequired,
  titleDecoration: PropTypes.node,
  moreButtonVisible: PropTypes.bool,
  moreButtonLabel: PropTypes.string,
  hiddenButtonVisible: PropTypes.bool,
  hiddenButtonLabel: PropTypes.string,
  onMoreButtonClicked: PropTypes.func,
  footerVisible: PropTypes.bool,
  footerActionLabel: PropTypes.string,
  onFooterActionClicked: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

Collapsable.defaultProps = {
  titleDecoration: null,
  expandedByDefault: false,
  moreButtonVisible: false,
  moreButtonLabel: '',
  hiddenButtonVisible: false,
  hiddenButtonLabel: '',
  onMoreButtonClicked: () => {},
  footerVisible: false,
  footerActionLabel: '',
  onFooterActionClicked: () => {},
};

export default Collapsable;
